/**
 * VoteConfirmationModal Component
 *
 * Story FOS-4.2.7: VotingPage Implementation
 * Task 8: Create VoteConfirmationModal component
 *
 * Confirmation dialog for voting with proposal summary.
 *
 * @see AC-4.2.7.1 - Modal shows "You are voting [CHOICE] on: [Title]"
 * @see AC-4.2.7.2 - Success shows vote-pass NFT ID
 *
 * @module components/governance/VoteConfirmationModal
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import type { VoteChoice } from '../../services/governanceService';

// =============================================================================
// Types
// =============================================================================

export interface VoteConfirmationModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback to close modal */
  onClose: () => void;
  /** Callback when vote is confirmed */
  onConfirm: () => Promise<{ success: boolean; vote_pass_nft_id?: number; error?: string }>;
  /** Proposal ID */
  proposalId: number;
  /** Proposal title */
  proposalTitle: string;
  /** Selected vote choice */
  voteChoice: VoteChoice;
  /** Feedback text (for NeedsRefinement) */
  feedback?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function getVoteLabel(choice: VoteChoice): string {
  switch (choice) {
    case 'Yes':
      return 'YES';
    case 'No':
      return 'NO';
    case 'Abstain':
      return 'ABSTAIN';
    case 'NeedsRefinement':
      return 'NEEDS REFINEMENT';
    default:
      return choice;
  }
}

function getVoteColorClass(choice: VoteChoice): string {
  switch (choice) {
    case 'Yes':
      return 'text-green-600 dark:text-green-400';
    case 'No':
      return 'text-red-600 dark:text-red-400';
    case 'Abstain':
      return 'text-gray-600 dark:text-gray-400';
    case 'NeedsRefinement':
      return 'text-yellow-600 dark:text-yellow-400';
    default:
      return '';
  }
}

// =============================================================================
// Component
// =============================================================================

/**
 * Confirmation modal for vote submission
 */
export function VoteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  proposalId,
  proposalTitle,
  voteChoice,
  feedback,
}: VoteConfirmationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    nftId?: number;
    error?: string;
  } | null>(null);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setResult(null);

    try {
      const response = await onConfirm();
      setResult({
        success: response.success,
        nftId: response.vote_pass_nft_id,
        error: response.error,
      });
    } catch (e) {
      setResult({
        success: false,
        error: e instanceof Error ? e.message : 'Failed to submit vote',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setResult(null);
      onClose();
    }
  }, [isSubmitting, onClose]);

  // Handle Escape key to close modal (accessibility)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isSubmitting, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="vote-confirmation-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        data-testid="modal-backdrop"
      />

      {/* Modal */}
      <div className="relative bg-card rounded-lg border border-border shadow-lg w-full max-w-md mx-4 p-6">
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground disabled:opacity-50"
          data-testid="modal-close-button"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        {result?.success ? (
          // Success state
          <div className="text-center py-4" data-testid="modal-success">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Vote Recorded!
            </h2>
            <p className="text-muted-foreground mb-4">
              Your vote has been successfully recorded.
            </p>
            {result.nftId && (
              <div
                className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-green-700 dark:text-green-300"
                data-testid="vote-nft-id"
              >
                Vote-pass NFT: <strong>#{result.nftId}</strong>
              </div>
            )}
            <button
              onClick={handleClose}
              className="mt-6 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              data-testid="modal-done-button"
            >
              Done
            </button>
          </div>
        ) : result?.error ? (
          // Error state
          <div className="text-center py-4" data-testid="modal-error">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Vote Failed
            </h2>
            <p className="text-red-600 dark:text-red-400 mb-4">
              {result.error}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setResult(null)}
                className="px-6 py-2 border border-border rounded-lg hover:bg-muted/50"
                data-testid="modal-retry-button"
              >
                Try Again
              </button>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          // Confirmation state
          <>
            <h2
              className="text-xl font-semibold text-foreground mb-4 pr-8"
              data-testid="modal-title"
            >
              Confirm Your Vote
            </h2>

            <div className="mb-6">
              <p className="text-foreground" data-testid="modal-vote-summary">
                You are voting{' '}
                <span className={`font-bold ${getVoteColorClass(voteChoice)}`}>
                  {getVoteLabel(voteChoice)}
                </span>{' '}
                on:
              </p>
              <p
                className="text-muted-foreground mt-2"
                data-testid="modal-proposal-title"
              >
                #{proposalId}: {proposalTitle}
              </p>

              {voteChoice === 'NeedsRefinement' && feedback && (
                <div
                  className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg"
                  data-testid="modal-feedback-summary"
                >
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium mb-1">
                    Your feedback:
                  </p>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    {feedback}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-muted/50 disabled:opacity-50"
                data-testid="modal-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="modal-confirm-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Confirm Vote'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default VoteConfirmationModal;
